import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import {
  MobileStateQuestion,
  SaveDataAnswerGroup,
} from '@safeguardproperties/corescript-react-npm/dist/types';
import { useEffect, useMemo, useState } from 'react';
import { DateFormat } from '../../constants';
import dayjs, { Dayjs } from 'dayjs';
import { debounce } from '../../utils';
import { MobileDatePicker } from '@mui/x-date-pickers';
import { styled } from '@mui/material/styles';
import { Platform } from '../../constants/platform';

const StyledPaper = styled('div')(({ theme }) => ({
  maxHeight: '300px', // Adjust the calendar height
  transform: 'translate(-20px, 0px)', // Move calendar left
}));

interface CustomDatePickerProps {
  question: MobileStateQuestion;
  answers: SaveDataAnswerGroup | undefined;
  disabled?: boolean;
  stringMapper: (key: string) => string;
  onSetAnswer: (questionKey: string, answers: string[]) => void;
}

const CustomDatePicker = ({
  question,
  answers,
  stringMapper,
  onSetAnswer,
  disabled,
}: CustomDatePickerProps) => {
  const [dateVisible, setDateVisible] = useState<boolean>(false);
  const [userAns, setUserAns] = useState('');
  const [date, setDate] = useState<string>('');
  const [isAnswered, setIsAnswered] = useState<boolean>(question.Completed);

  const dateValidation = {
    maximum: question?.DateRange?.Max
      ? dayjs(question.DateRange.Max)
      : dayjs().add(1, 'year'), //
    minimum: question?.DateRange?.Min
      ? dayjs(question.DateRange.Min)
      : dayjs('2024-01-01'),
  };

  const label = useMemo(() => {
    if (stringMapper) {
      return stringMapper(question?.Title);
    }
    return question?.Title;
  }, [question, stringMapper]);

  useEffect(() => {
    // eslint-disable-next-line no-unsafe-optional-chaining
    if (answers?.Answers[0] !== undefined && 'Answer' in answers?.Answers[0]) {
      setUserAns(dayjs(answers?.Answers[0]?.Answer).format('MM/DD/YYYY'));
    }
  }, [answers]);

  const processDateSelection = (dateValue: Date) => {
    const selectedDate = dayjs(dateValue).format(DateFormat.default);
    setDate(selectedDate);
    setUserAns(selectedDate);
    if (question.Type === 'DateTime') {
      // setTimePickerVisible(true);
    } else {
      setIsAnswered(true);
      if (onSetAnswer) onSetAnswer(question.QuestionKey, [selectedDate]);
    }
  };

  const onSelectDate = debounce((date: dayjs.Dayjs | null) => {
    if (date) {
      processDateSelection(date.toDate());
    }
  }, 10);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DemoContainer components={['DatePicker']}>
        <MobileDatePicker
          disabled={disabled}
          label={Platform?.isAudit() ? label : null}
          onAccept={onSelectDate}
          minDate={dateValidation.minimum}
          maxDate={dateValidation.maximum}
          value={dayjs(userAns, 'MM/DD/YYYY') ?? ''}
          slotProps={{
            mobilePaper: {
              sx: {
                // width: '100%',
                // maxWidth: '360px',
                height: '400px',
                position: 'absolute',
                left: '0',
                transform: 'translateX(300px)',
                // Hide the toolbar section with date display
                '& .MuiPickersToolbar-root.MuiDatePickerToolbar-root': {
                  display: 'none',
                },
                '& .MuiPickersCalendarHeader-root': {
                  paddingLeft: '20px',
                  paddingRight: '20px',
                },
                '& .MuiDayPicker-header': {
                  paddingLeft: '20px',
                  paddingRight: '20px',
                },
                '& .MuiDayPicker-monthContainer': {
                  paddingLeft: '20px',
                  paddingRight: '20px',
                },
                textField: {
                  sx: {
                    cursor: 'pointer',
                  },
                },
              },
            },
          }}
        />
      </DemoContainer>
    </LocalizationProvider>
  );
};

export default CustomDatePicker;
